import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import ProfilePage from './pages/ProfilePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import FavoritesPage from './pages/FavoritesPage';
import AuthPage from './pages/AuthPage';
import DesignPage from './pages/DesignPage';

// Legal Pages
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import CookiePage from './pages/CookiePage';
import DistanceSalesContractPage from './pages/DistanceSalesContractPage';
import MembershipAgreementPage from './pages/MembershipAgreementPage';
import ReturnCancelPage from './pages/ReturnCancelPage';
import PreliminaryInformationPage from './pages/PreliminaryInformationPage';
import LegalPage from './pages/LegalPage';

// Admin Components
import {
  AdminLayout,
  AdminLogin,
  AdminDashboard,
  ProductsPage as AdminProductsPage,
  CategoriesPage,
  OrdersPage,
  UsersPage,
  SettingsPage
} from './components/admin';
import AddProductPage from './components/admin/AddProductPage';
import EditProductPage from './components/admin/EditProductPage';
import ProductSetsPage from './components/admin/ProductSetsPage';
import AddProductSetPage from './components/admin/AddProductSetPage';
import PackagesPage from './components/admin/PackagesPage';
import AddPackagePage from './components/admin/AddPackagePage';

// Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import CategoryNavigator from './components/CategoryNavigator';
import ScrollToTop from './components/ScrollToTop';

// Context
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { AdminAuthProvider } from './context/AdminAuthContext';

function App() {
  return (
    <Router>
        <ScrollToTop />
        <Routes>
        {/* Admin Routes - Tamamen ayrı layout ve authentication, AuthProvider'ın dışında */}
          <Route path="/admin/login" element={
            <AdminAuthProvider>
              <AdminLogin />
            </AdminAuthProvider>
          } />
          <Route path="/admin" element={
            <AdminAuthProvider>
              <AdminLayout />
            </AdminAuthProvider>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProductsPage />} />
            <Route path="products/add" element={<AddProductPage />} />
          <Route path="products/edit/:id" element={<EditProductPage />} />
            <Route path="product-sets" element={<ProductSetsPage />} />
            <Route path="product-sets/add" element={<AddProductSetPage />} />
            <Route path="packages" element={<PackagesPage />} />
            <Route path="packages/add" element={<AddPackagePage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

        {/* 3D Design Page - Full screen without header/footer */}
        <Route path="/design" element={
          <AuthProvider>
            <CartProvider>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <DesignPage />
              </motion.div>
            </CartProvider>
          </AuthProvider>
        } />

          {/* Main Site Routes - Ana site layout'u */}
          <Route path="/*" element={
          <AuthProvider>
            <CartProvider>
            <div className="min-h-screen bg-white flex flex-col">
              <Header />
              <CategoryNavigator />
              
              <main className="flex-grow">
                <AnimatePresence mode="wait">
                  <Routes>
                    <Route 
                      path="/" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <HomePage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/products" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <ProductsPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/products/:id" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <ProductDetailPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/cart" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <CartPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/checkout" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <CheckoutPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/profile" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <ProfilePage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/about" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <AboutPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/contact" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <ContactPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/favorites" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <FavoritesPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/auth" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <AuthPage />
                        </motion.div>
                      } 
                    />
                    
                    {/* Legal Pages */}
                    <Route 
                      path="/privacy" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <PrivacyPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/terms" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <TermsPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/cookies" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <CookiePage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/distance-sales-contract" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <DistanceSalesContractPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/membership-agreement" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <MembershipAgreementPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/return-cancel" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <ReturnCancelPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/preliminary-information" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <PreliminaryInformationPage />
                        </motion.div>
                      } 
                    />
                    <Route 
                      path="/legal" 
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5 }}
                        >
                          <LegalPage />
                        </motion.div>
                      } 
                    />
                  </Routes>
                </AnimatePresence>
              </main>

              <Footer />
            </div>
            </CartProvider>
          </AuthProvider>
          } />
        </Routes>
    </Router>
  );
}

export default App;
